import {FolderId, ProjectFile} from '@codebridge/types';

import codebridgeI18n from '@cdo/apps/codebridge/locale';
import {MultiFileSource} from '@cdo/apps/lab2/types';

import {isDuplicateFileName, DuplicateFileError} from './isDuplicateFileName';
import {isValidFileName} from './isValidFileName';

type ValidateFileNameArgs = {
  fileName: string;
  folderId: FolderId;
  projectFiles: MultiFileSource['files'];
  isStartMode: boolean;
  validationFile: ProjectFile | undefined;
  validFileTypes?: string[];
  selectedFileType?: string;
};

/**
 * Validates a file name. Internally, this checks to confirm that the file has an extension, the file name does not already exist
   in the parent folder, and that the file name is valid. Be aware that an empty string is considered a valid name.
   If the optional validFileTypes list is passed, this checks whether file extension is included in the list.
 *
 * @param args An object containing the following properties:
 *   - **fileName:** The name of the file to validate.
 *   - **folderId:** The ID of the parent folder.
 *   - **projectFiles:** An array of project files.
 *   - **isStartMode:** Indicates if the application is in start mode.
 *   - **validationFile:** The validation file.
 *   - **validFileTypes:** List of valid file types (optional)
 *   - **selectedFileType:** The file type selected from a dropdown (optional).
 *     When provided, this is used instead of extracting the extension from the
 *     file name for validation against validFileTypes.
 * @returns A string error message if the file name is invalid, or undefined if the file name is valid.
 */
export const validateFileName = ({
  fileName,
  folderId,
  projectFiles,
  isStartMode,
  validationFile,
  validFileTypes,
  selectedFileType,
}: ValidateFileNameArgs) => {
  if (!fileName.length) {
    return;
  }

  // If selectedFileType is provided, use it for validation instead of extracting from filename.
  // This allows the file extension to come from a dropdown selection rather than requiring
  // users to type the extension in the filename.
  if (selectedFileType) {
    // Skip extension validation since the extension comes from the dropdown.
  } else {
    const extension = fileName.split('.').pop();
    if (!extension || extension === fileName) {
      return codebridgeI18n.noFileExtensionError();
    }

    if (validFileTypes && !validFileTypes.includes(extension)) {
      const validFileTypesString = validFileTypes.join(', ');
      return `${codebridgeI18n.invalidFileType({
        fileType: extension,
      })} ${codebridgeI18n.validFileTypesInfo({
        validFileTypes: validFileTypesString,
      })}`;
    }
  }

  // When the extension comes from the dropdown, the base name must not carry
  // its own period — otherwise "My.Class" + dropdown "java" yields the
  // nonsense "My.Class.java". Passing hasDropdown enforces the no-period rule.
  if (!isValidFileName(fileName, Boolean(selectedFileType))) {
    return codebridgeI18n.invalidNameError();
  }

  // When the extension comes from the dropdown, `fileName` is just the base
  // name. The duplicate check compares against full file names, so recombine
  // before checking.
  const fullFileName = selectedFileType
    ? `${fileName}.${selectedFileType}`
    : fileName;

  const duplicateFileError = isDuplicateFileName({
    fileName: fullFileName,
    folderId,
    projectFiles,
    isStartMode,
    validationFile,
  });

  if (duplicateFileError) {
    if (duplicateFileError === DuplicateFileError.DUPLICATE_SUPPORT_FILE) {
      return codebridgeI18n.duplicateSupportFileError({fileName: fullFileName});
    } else {
      return codebridgeI18n.duplicateFileError({fileName: fullFileName});
    }
  }
};

export const validateFileNameForModal = (
  args: ValidateFileNameArgs
): {text: string; type: 'error' | 'warning'} | undefined => {
  const errorMessage = validateFileName(args);
  if (errorMessage) {
    return {
      text: errorMessage,
      type: 'error',
    };
  }
};
